// apps-kb-navigation-logic.js - Pure logical navigation in rank/file space
// This module handles all navigation logic without any pixel coordinate dependencies

import state from './apps-state.js';
import * as render from './apps-render.js';
import * as helpers from './apps-kb-helpers.js';

/**
 * Extended rank/file coordinate system:
 * 
 * RANKS:
 * -1.0   = Navigation bar (main) / Modal overlay above folder title (folder)
 * -0.5   = Folder title (folder only)
 * 0-MAX  = Actual icon ranks
 * MAX+1  = Page controls/slider (main) / Modal overlay below folder (folder)
 * 
 * FILES:
 * -1.0     = Left scroll zone
 * -0.5     = Insert before first icon (file 0)
 * 0-MAX    = Icon slots
 * 0.5-MAX  = Insert between icons (e.g., 0.5 = between file 0 and 1)
 * MAX+0.5  = Insert after last icon (file MAX)
 * MAX+1    = Right scroll zone
 */

// ===== POSITION TYPE CHECKING =====

export function isSpecialRank(rank) {
    return rank === -1 || rank === -0.5 || !Number.isInteger(rank);
}

export function isScrollZone(file) {
    return file === -1 || file === Infinity; // Using Infinity for MAX+1
}

export function isInsertPosition(file) {
    return !Number.isInteger(file) && file >= -0.5;
}

export function isIconSlot(rank, file, maxRank, maxFile) {
    return Number.isInteger(rank) && rank >= 0 && rank <= maxRank &&
           Number.isInteger(file) && file >= 0 && file <= maxFile;
}

// ===== BOUNDARY CALCULATIONS =====

export function getMaxRank(layout, isFolder, inMoveMode) {
    if (isFolder) {
        if (!state.openFolder) return 2;
        
        const folderIcons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
        const maxGrandRank = getMaxOccupiedGrandRank(folderIcons);
        
        if (inMoveMode) {
            // In move mode, allow one additional rank beyond occupied (but max 2)
            return Math.min(maxGrandRank + 1, 2);
        } else {
            // In browse mode, only occupied ranks
            return Math.max(maxGrandRank, 0);
        }
    }
    return (layout.bands * 3) - 1;
}

export function getMaxFile(layout) {
    return (layout.leaves * 4) - 1;
}

function getMaxOccupiedGrandRank(icons) {
    let maxGrandRank = -1;
    icons.forEach(icon => {
        const grandRank = Math.floor(icon.position / 4);
        if (grandRank > maxGrandRank) maxGrandRank = grandRank;
    });
    return maxGrandRank;
}

// ===== OCCUPANCY CHECKING =====

export function isRankOccupied(rank, icons, layout, isFolder, currentScreen) {
    if (!Number.isInteger(rank) || rank < 0) return false;
    
    // Temporary screens are always empty
    if (state.temporaryScreenSide !== null) return false;
    
    const totalFiles = layout.leaves * 4;
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    
    for (let file = 0; file < totalFiles; file++) {
        const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
        if (!slotInfo) continue;
        
        const actualRegion = screenStartRegion + slotInfo.region;
        if (icons.some(icon => icon.region === actualRegion && icon.position === slotInfo.position)) {
            return true;
        }
    }
    return false;
}

export function isFileOccupied(file, icons, layout, isFolder, currentScreen) {
    if (!Number.isInteger(file) || file < 0) return false;
    
    // Temporary screens are always empty
    if (state.temporaryScreenSide !== null) return false;
    
    const ranksPerScreen = isFolder ? 3 : layout.bands * 3;
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    
    for (let rank = 0; rank < ranksPerScreen; rank++) {
        const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
        if (!slotInfo) continue;
        
        const actualRegion = screenStartRegion + slotInfo.region;
        if (icons.some(icon => icon.region === actualRegion && icon.position === slotInfo.position)) {
            return true;
        }
    }
    return false;
}

export function isSlotOccupied(rank, file, icons, layout, isFolder, currentScreen) {
    if (!Number.isInteger(rank) || !Number.isInteger(file)) return false;
    if (rank < 0 || file < 0) return false;
    
    // Temporary screens are always empty
    if (state.temporaryScreenSide !== null) return false;
    
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
    
    if (!slotInfo) return false;
    
    const actualRegion = screenStartRegion + slotInfo.region;
    return icons.some(icon => icon.region === actualRegion && icon.position === slotInfo.position);
}

// ===== RANK/FILE TO REGION/POSITION CONVERSION =====

function rankFileToRegionPosition(rank, file, layout, isFolder) {
    if (isFolder) {
        const regionLeaf = Math.floor(file / 4);
        const region = regionLeaf;
        const posRank = rank % 3;
        const posFile = file % 4;
        const position = posRank * 4 + posFile;
        return { region, position };
    }
    
    return render.visualPositionToRegionPosition(rank, file, layout);
}

// ===== SCROLL ZONE CHECKING =====

export function hasLeftScrollZone(currentScreen) {
    return currentScreen > 0;
}

export function hasRightScrollZone(icons, layout, isFolder, currentScreen) {
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const maxRegion = Math.max(...icons.map(icon => icon.region), -1);
    const maxScreen = Math.floor(maxRegion / regionsPerScreen);
    return currentScreen < maxScreen;
}

export function isOnTemporaryScreen(currentScreen) {
    return state.temporaryScreenSide !== null;
}

// ===== RANK NAVIGATION HELPERS =====

export function getNextOccupiedRank(currentRank, direction, icons, layout, isFolder, currentScreen) {
    const maxRank = getMaxRank(layout, isFolder, false);
    const step = direction === 'up' ? -1 : 1;
    
    let testRank = currentRank + step;
    
    while (testRank >= 0 && testRank <= maxRank) {
        if (isRankOccupied(testRank, icons, layout, isFolder, currentScreen)) {
            return testRank;
        }
        testRank += step;
    }
    
    return null;
}

export function getNextOccupiedFile(currentFile, direction, icons, layout, isFolder, currentScreen) {
    const maxFile = getMaxFile(layout);
    const step = direction === 'left' ? -1 : 1;
    
    let testFile = currentFile + step;
    
    while (testFile >= 0 && testFile <= maxFile) {
        if (isFileOccupied(testFile, icons, layout, isFolder, currentScreen)) {
            return testFile;
        }
        testFile += step;
    }
    
    return null;
}

export function getMinOccupiedRank(icons, layout, isFolder, currentScreen) {
    const maxRank = getMaxRank(layout, isFolder, false);
    for (let rank = 0; rank <= maxRank; rank++) {
        if (isRankOccupied(rank, icons, layout, isFolder, currentScreen)) {
            return rank;
        }
    }
    return null;
}

export function getMaxOccupiedRank(icons, layout, isFolder, currentScreen) {
    const maxRank = getMaxRank(layout, isFolder, false);
    for (let rank = maxRank; rank >= 0; rank--) {
        if (isRankOccupied(rank, icons, layout, isFolder, currentScreen)) {
            return rank;
        }
    }
    return null;
}

export function getMinOccupiedFile(icons, layout, isFolder, currentScreen) {
    const maxFile = getMaxFile(layout);
    for (let file = 0; file <= maxFile; file++) {
        if (isFileOccupied(file, icons, layout, isFolder, currentScreen)) {
            return file;
        }
    }
    return null;
}

export function getMaxOccupiedFile(icons, layout, isFolder, currentScreen) {
    const maxFile = getMaxFile(layout);
    for (let file = maxFile; file >= 0; file--) {
        if (isFileOccupied(file, icons, layout, isFolder, currentScreen)) {
            return file;
        }
    }
    return null;
}

// ===== INSERT POSITION VALIDATION (MOVE MODE) =====

export function isValidInsertPosition(rank, file, draggedIcon, icons, layout, isFolder, currentScreen) {
    if (!isInsertPosition(file)) return false;
    if (!Number.isInteger(rank) || rank < 0) return false;
    
    const maxFile = getMaxFile(layout);
    
    // Check if rank has space
    if (!rankHasSpaceForInsert(rank, draggedIcon, icons, layout, isFolder, currentScreen)) {
        return false;
    }
    
    // Check adjacency to source position
    if (isAdjacentToSourcePosition(rank, file, draggedIcon, layout, isFolder, currentScreen)) {
        return false;
    }
    
    // Validate specific insert position types
    if (file === -0.5) {
        // Insert before first icon - file 0 must be occupied
        return isSlotOccupied(rank, 0, icons, layout, isFolder, currentScreen);
    } else if (file === maxFile + 0.5) {
        // Insert after last icon - file MAX must be occupied
        return isSlotOccupied(rank, maxFile, icons, layout, isFolder, currentScreen);
    } else if (file > 0 && file < maxFile + 0.5) {
        // Insert between icons - both adjacent files must be occupied
        const leftFile = Math.floor(file);
        const rightFile = Math.ceil(file);
        return isSlotOccupied(rank, leftFile, icons, layout, isFolder, currentScreen) &&
               isSlotOccupied(rank, rightFile, icons, layout, isFolder, currentScreen);
    }
    
    return false;
}

function rankHasSpaceForInsert(rank, draggedIcon, icons, layout, isFolder, currentScreen) {
    // Count occupied slots in this rank (excluding the dragged icon)
    const maxFile = getMaxFile(layout);
    let occupiedCount = 0;
    
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    
    // Get source region/position for comparison
    const sourceRegion = draggedIcon.region;
    const sourcePosition = draggedIcon.position;
    
    for (let file = 0; file <= maxFile; file++) {
        const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
        if (!slotInfo) continue;
        
        const actualRegion = screenStartRegion + slotInfo.region;
        const icon = icons.find(icon => 
            icon.region === actualRegion && 
            icon.position === slotInfo.position
        );
        
        if (icon && !(icon.region === sourceRegion && icon.position === sourcePosition)) {
            occupiedCount++;
        }
    }
    
    return occupiedCount < (maxFile + 1);
}

function isAdjacentToSourcePosition(rank, file, draggedIcon, layout, isFolder, currentScreen) {
    // Calculate source visual position
    const sourceRegion = draggedIcon.region;
    const sourcePosition = draggedIcon.position;
    
    let sourceRank, sourceFile, sourceScreen;
    if (isFolder) {
        sourceRank = Math.floor(sourcePosition / 4);
        sourceFile = sourceRegion * 4 + (sourcePosition % 4);
        const regionsPerScreen = layout.leaves;
        sourceScreen = Math.floor(sourceRegion / regionsPerScreen);
    } else {
        const visualPos = render.calculateVisualPosition(sourceRegion, sourcePosition, layout);
        const regionsPerScreen = layout.leaves * layout.bands;
        const screenNum = Math.floor(sourceRegion / regionsPerScreen);
        const regionWithinScreen = sourceRegion % regionsPerScreen;
        const regionBand = regionWithinScreen % layout.bands;
        const regionLeaf = Math.floor(regionWithinScreen / layout.bands);
        sourceRank = regionBand * 3 + Math.floor(sourcePosition / 4);
        sourceFile = regionLeaf * 4 + (sourcePosition % 4);
        sourceScreen = screenNum;
    }
    
    // Check if we're on same screen - if not, we're definitely not adjacent
    if (currentScreen !== sourceScreen) return false;
    
    // Check if we're on same rank
    if (rank !== sourceRank) return false;
    
    // Check if file is within ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â±0.5 of source file
    return Math.abs(file - sourceFile) <= 0.5;
}

// ===== MAIN NAVIGATION LOGIC =====

/**
 * Get the next valid navigation position from current position in given direction
 * Returns { rank, file } or null if no valid position exists
 */
export function getNextNavigablePosition(currentRank, currentFile, direction, browseMode, inMoveMode, icons, layout, isFolder, currentScreen, draggedIcon = null) {
    const maxRank = getMaxRank(layout, isFolder, inMoveMode);
    const maxFile = getMaxFile(layout);
    
    // Handle special zones first
    if (currentRank === -1) {
        return handleNavigationFromTopZone(direction, browseMode, inMoveMode, icons, layout, isFolder, currentScreen);
    }
    
    if (currentRank === -0.5) {
        return handleNavigationFromFolderTitle(direction, browseMode, inMoveMode, icons, layout, isFolder, currentScreen);
    }
    
    if (currentRank === maxRank + 1 || (isFolder && currentRank > maxRank)) {
        return handleNavigationFromBottomZone(direction, browseMode, inMoveMode, icons, layout, isFolder, currentScreen, maxRank);
    }
    
    // Handle scroll zones
    if (currentFile === -1) {
        return handleNavigationFromLeftScrollZone(direction, currentRank, icons, layout, isFolder, currentScreen);
    }
    
    if (currentFile >= maxFile + 1) {
        return handleNavigationFromRightScrollZone(direction, currentRank, icons, layout, isFolder, currentScreen, maxFile);
    }
    
    // Regular navigation within icon grid
    switch (direction) {
        case 'up':
            return handleUpNavigation(currentRank, currentFile, browseMode, inMoveMode, icons, layout, isFolder, currentScreen, maxRank);
        case 'down':
            return handleDownNavigation(currentRank, currentFile, browseMode, inMoveMode, icons, layout, isFolder, currentScreen, maxRank);
        case 'left':
            return handleLeftNavigation(currentRank, currentFile, browseMode, inMoveMode, draggedIcon, icons, layout, isFolder, currentScreen, maxFile);
        case 'right':
            return handleRightNavigation(currentRank, currentFile, browseMode, inMoveMode, draggedIcon, icons, layout, isFolder, currentScreen, maxFile);
        default:
            return null;
    }
}

// ===== SPECIAL ZONE NAVIGATION =====

function handleNavigationFromTopZone(direction, browseMode, inMoveMode, icons, layout, isFolder, currentScreen) {
    if (direction === 'down') {
        // Move into folder title (folder only) or first rank
        if (isFolder && !inMoveMode) {
            return { rank: -0.5, file: 0 };
        }
        
        const minRank = browseMode && !inMoveMode ? 
            getMinOccupiedRank(icons, layout, isFolder, currentScreen) : 0;
        
        return { rank: minRank ?? 0, file: 0 };
    }
    
    // Left/right/up stay in top zone
    return null;
}

function handleNavigationFromFolderTitle(direction, browseMode, inMoveMode, icons, layout, isFolder, currentScreen) {
    if (direction === 'up') {
        // Go to top zone
        return { rank: -1, file: 0 };
    }
    
    if (direction === 'down') {
        // Go to first accessible rank
        const minRank = browseMode && !inMoveMode ? 
            getMinOccupiedRank(icons, layout, isFolder, currentScreen) : 0;
        
        return { rank: minRank ?? 0, file: 0 };
    }
    
    // Left/right stay on title
    return null;
}

function handleNavigationFromBottomZone(direction, browseMode, inMoveMode, icons, layout, isFolder, currentScreen, maxRank) {
    if (direction === 'up') {
        // Move to last accessible rank
        const lastRank = browseMode && !inMoveMode ? 
            getMaxOccupiedRank(icons, layout, isFolder, currentScreen) : maxRank;
        
        return { rank: lastRank ?? 0, file: 0 };
    }
    
    // Down/left/right stay in bottom zone
    return null;
}

function handleNavigationFromLeftScrollZone(direction, currentRank, icons, layout, isFolder, currentScreen) {
    if (direction === 'right') {
        // Enter the grid at the leftmost occupied file
        const minFile = getMinOccupiedFile(icons, layout, isFolder, currentScreen);
        return { rank: currentRank, file: minFile ?? 0 };
    }
    
    if (direction === 'up' || direction === 'down') {
        // Move vertically within scroll zone
        const maxRank = getMaxRank(layout, isFolder, true); // Always use move mode logic for scroll zones
        let newRank = currentRank + (direction === 'down' ? 1 : -1);
        
        // Wrap around
        if (newRank < 0) {
            newRank = maxRank;
        } else if (newRank > maxRank) {
            newRank = 0;
        }
        
        return { rank: newRank, file: -1 };
    }
    
    // Left stays in scroll zone (or triggers actual scroll)
    return null;
}

function handleNavigationFromRightScrollZone(direction, currentRank, icons, layout, isFolder, currentScreen, maxFile) {
    if (direction === 'left') {
        // Enter the grid at the rightmost occupied file
        const maxOccFile = getMaxOccupiedFile(icons, layout, isFolder, currentScreen);
        return { rank: currentRank, file: maxOccFile ?? maxFile };
    }
    
    if (direction === 'up' || direction === 'down') {
        // Move vertically within scroll zone
        const maxRank = getMaxRank(layout, isFolder, true); // Always use move mode logic for scroll zones
        let newRank = currentRank + (direction === 'down' ? 1 : -1);
        
        // Wrap around
        if (newRank < 0) {
            newRank = maxRank;
        } else if (newRank > maxRank) {
            newRank = 0;
        }
        
        return { rank: newRank, file: maxFile + 1 };
    }
    
    // Right stays in scroll zone (or triggers actual scroll)
    return null;
}

// ===== VERTICAL NAVIGATION =====

/**
 * Determine the target file when moving vertically.
 * Try to keep the exact file (even if fractional), but fall back to rounding if invalid.
 */
function getTargetFileForVerticalMovement(currentFile, newRank, inMoveMode, draggedIcon, icons, layout, isFolder, currentScreen) {
    // If not in move mode or not an insert position, no special handling needed
    if (!inMoveMode || !isInsertPosition(currentFile)) {
        return currentFile;
    }
    
    // Check if the exact fractional file is valid at the new rank
    if (isValidInsertPosition(newRank, currentFile, draggedIcon, icons, layout, isFolder, currentScreen)) {
        return currentFile; // Keep the fractional position
    }
    
    // Fall back to rounding to integer file
    return Math.floor(currentFile);
}

function handleUpNavigation(currentRank, currentFile, browseMode, inMoveMode, icons, layout, isFolder, currentScreen, maxRank) {
    let newRank = currentRank - 1;
    
    if (newRank < 0) {
        // Handle wrapping or special zones
        if (isFolder && !inMoveMode) {
            // Browse mode in folder: go to title
            newRank = -0.5;
        } else if (isFolder && inMoveMode) {
            // Move mode in folder: go to top zone
            newRank = -1;
        } else if (!isFolder && !browseMode) {
            // Move mode on main: wrap to bottom
            newRank = maxRank;
        } else {
            // Browse mode on main: wrap to max occupied rank
            const maxOccRank = getMaxOccupiedRank(icons, layout, isFolder, currentScreen);
            newRank = maxOccRank ?? 0;
        }
    }
    
    // Browse mode: skip empty ranks (only if we're still in regular rank range)
    if (browseMode && !inMoveMode && newRank >= 0 && newRank <= maxRank) {
        while (newRank >= 0 && !isRankOccupied(newRank, icons, layout, isFolder, currentScreen)) {
            newRank--;
        }
        
        if (newRank < 0) {
            // Wrap around
            if (isFolder) {
                newRank = -0.5;
            } else {
                const maxOccRank = getMaxOccupiedRank(icons, layout, isFolder, currentScreen);
                newRank = maxOccRank ?? 0;
            }
        }
    }
    
    // Now that we know the final newRank, determine the target file
    const targetFile = getTargetFileForVerticalMovement(
        currentFile, 
        newRank, 
        inMoveMode, 
        inMoveMode ? (state.dragState?.icon || null) : null,
        icons, 
        layout, 
        isFolder, 
        currentScreen
    );
    
    return { rank: newRank, file: targetFile };
}

function handleDownNavigation(currentRank, currentFile, browseMode, inMoveMode, icons, layout, isFolder, currentScreen, maxRank) {
    let newRank = currentRank + 1;
    
    if (newRank > maxRank) {
        // Handle wrapping or special zones
        if (isFolder) {
            // Folder: go to bottom zone
            newRank = maxRank + 1;
        } else if (!browseMode && inMoveMode) {
            // Move mode on main: wrap to top
            newRank = 0;
        } else {
            // Browse mode on main: wrap to min occupied rank
            const minOccRank = getMinOccupiedRank(icons, layout, isFolder, currentScreen);
            newRank = minOccRank ?? 0;
        }
    }
    
    // Browse mode: skip empty ranks (only if we're still in regular rank range)
    if (browseMode && !inMoveMode && newRank >= 0 && newRank <= maxRank) {
        while (newRank <= maxRank && !isRankOccupied(newRank, icons, layout, isFolder, currentScreen)) {
            newRank++;
        }
        
        if (newRank > maxRank) {
            // Wrap around
            if (isFolder) {
                newRank = maxRank + 1;
            } else {
                const minOccRank = getMinOccupiedRank(icons, layout, isFolder, currentScreen);
                newRank = minOccRank ?? 0;
            }
        }
    }
    
    // Now that we know the final newRank, determine the target file
    const targetFile = getTargetFileForVerticalMovement(
        currentFile, 
        newRank, 
        inMoveMode, 
        inMoveMode ? (state.dragState?.icon || null) : null,
        icons, 
        layout, 
        isFolder, 
        currentScreen
    );
    
    return { rank: newRank, file: targetFile };
}

// ===== HORIZONTAL NAVIGATION =====

function handleLeftNavigation(currentRank, currentFile, browseMode, inMoveMode, draggedIcon, icons, layout, isFolder, currentScreen, maxFile) {
    let newFile = currentFile - 1;
    
    // In move mode with fractional positions, need to handle half-steps
    if (inMoveMode && draggedIcon) {
        newFile = getNextLeftPosition(currentRank, currentFile, draggedIcon, icons, layout, isFolder, currentScreen, maxFile);
        
        if (newFile === null) {
            // No valid left position, handle wrapping
            if (hasLeftScrollZone(currentScreen)) {
                return { rank: currentRank, file: -1 };
            } else {
                // Wrap to rightmost position
                if (hasRightScrollZone(icons, layout, isFolder, currentScreen)) {
                    return { rank: currentRank, file: maxFile + 1 };
                } else {
                    const maxOccFile = getMaxOccupiedFile(icons, layout, isFolder, currentScreen);
                    return { rank: currentRank, file: maxOccFile ?? maxFile };
                }
            }
        }
        
        return { rank: currentRank, file: newFile };
    }
    
    // Browse mode or no dragged icon
    if (newFile < 0) {
        if (hasLeftScrollZone(currentScreen)) {
            return { rank: currentRank, file: -1 };
        } else {
            // Wrap to rightmost occupied file
            const maxOccFile = getMaxOccupiedFile(icons, layout, isFolder, currentScreen);
            return { rank: currentRank, file: maxOccFile ?? maxFile };
        }
    }
    
    // Skip empty files in browse mode
    if (browseMode && !inMoveMode) {
        while (newFile >= 0 && !isFileOccupied(newFile, icons, layout, isFolder, currentScreen)) {
            newFile--;
        }
        
        if (newFile < 0) {
            if (hasLeftScrollZone(currentScreen)) {
                return { rank: currentRank, file: -1 };
            } else {
                const maxOccFile = getMaxOccupiedFile(icons, layout, isFolder, currentScreen);
                return { rank: currentRank, file: maxOccFile ?? maxFile };
            }
        }
    }
    
    return { rank: currentRank, file: newFile };
}

function handleRightNavigation(currentRank, currentFile, browseMode, inMoveMode, draggedIcon, icons, layout, isFolder, currentScreen, maxFile) {
    let newFile = currentFile + 1;
    
    // In move mode with fractional positions, need to handle half-steps
    if (inMoveMode && draggedIcon) {
        newFile = getNextRightPosition(currentRank, currentFile, draggedIcon, icons, layout, isFolder, currentScreen, maxFile);
        
        if (newFile === null) {
            // No valid right position, handle wrapping
            if (hasRightScrollZone(icons, layout, isFolder, currentScreen)) {
                return { rank: currentRank, file: maxFile + 1 };
            } else {
                // Wrap to leftmost position
                if (hasLeftScrollZone(currentScreen)) {
                    return { rank: currentRank, file: -1 };
                } else {
                    const minOccFile = getMinOccupiedFile(icons, layout, isFolder, currentScreen);
                    return { rank: currentRank, file: minOccFile ?? 0 };
                }
            }
        }
        
        return { rank: currentRank, file: newFile };
    }
    
    // Browse mode or no dragged icon
    if (newFile > maxFile) {
        if (hasRightScrollZone(icons, layout, isFolder, currentScreen)) {
            return { rank: currentRank, file: maxFile + 1 };
        } else {
            // Wrap to leftmost occupied file
            const minOccFile = getMinOccupiedFile(icons, layout, isFolder, currentScreen);
            return { rank: currentRank, file: minOccFile ?? 0 };
        }
    }
    
    // Skip empty files in browse mode
    if (browseMode && !inMoveMode) {
        while (newFile <= maxFile && !isFileOccupied(newFile, icons, layout, isFolder, currentScreen)) {
            newFile++;
        }
        
        if (newFile > maxFile) {
            if (hasRightScrollZone(icons, layout, isFolder, currentScreen)) {
                return { rank: currentRank, file: maxFile + 1 };
            } else {
                const minOccFile = getMinOccupiedFile(icons, layout, isFolder, currentScreen);
                return { rank: currentRank, file: minOccFile ?? 0 };
            }
        }
    }
    
    return { rank: currentRank, file: newFile };
}

// ===== MOVE MODE FRACTIONAL POSITION NAVIGATION =====

function getNextLeftPosition(rank, currentFile, draggedIcon, icons, layout, isFolder, currentScreen, maxFile) {
    const validPositions = getAllValidHorizontalPositions(rank, draggedIcon, icons, layout, isFolder, currentScreen, maxFile);
    
    // Find current position in list
    let currentIndex = -1;
    for (let i = 0; i < validPositions.length; i++) {
        if (Math.abs(validPositions[i] - currentFile) < 0.01) {
            currentIndex = i;
            break;
        }
    }
    
    if (currentIndex <= 0) {
        return null; // No left position available
    }
    
    return validPositions[currentIndex - 1];
}

function getNextRightPosition(rank, currentFile, draggedIcon, icons, layout, isFolder, currentScreen, maxFile) {
    const validPositions = getAllValidHorizontalPositions(rank, draggedIcon, icons, layout, isFolder, currentScreen, maxFile);
    
    // Find current position in list
    let currentIndex = -1;
    for (let i = 0; i < validPositions.length; i++) {
        if (Math.abs(validPositions[i] - currentFile) < 0.01) {
            currentIndex = i;
            break;
        }
    }
    
    if (currentIndex < 0 || currentIndex >= validPositions.length - 1) {
        return null; // No right position available
    }
    
    return validPositions[currentIndex + 1];
}

export function getAllValidHorizontalPositions(rank, draggedIcon, icons, layout, isFolder, currentScreen, maxFile) {
    const positions = [];
    
    // In move mode, scroll zones are ALWAYS available to allow creating temporary screens,
    // EXCEPT when on a temporary screen going further in that direction
    const tempSide = state.temporaryScreenSide;
    
    // Left scroll zone: blocked ONLY if we're on the left temporary screen
    if (tempSide !== 'left') {
        positions.push(-1);
    }
    
    // Get all icons in this rank (excluding dragged icon)
    const rankIcons = [];
    
    // On temporary screens, rankIcons should stay empty (no icons present)
    if (tempSide === null) {
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const screenStartRegion = currentScreen * regionsPerScreen;
        
        for (let file = 0; file <= maxFile; file++) {
            const slotInfo = rankFileToRegionPosition(rank, file, layout, isFolder);
            if (!slotInfo) continue;
            
            const actualRegion = screenStartRegion + slotInfo.region;
            const icon = icons.find(icon => 
                icon.region === actualRegion && 
                icon.position === slotInfo.position
            );
            
            if (icon && icon.id !== draggedIcon.id) {
                rankIcons.push({ file, icon });
            }
        }
    }
    
    // Sort by file
    rankIcons.sort((a, b) => a.file - b.file);
    
    // If rank is empty, add all integer positions plus valid insert positions
    if (rankIcons.length === 0) {
        // Check if -0.5 is valid on empty rank (need file 0 to be occupied by dragged icon's original position)
        if (isValidInsertPosition(rank, -0.5, draggedIcon, icons, layout, isFolder, currentScreen)) {
            positions.push(-0.5);
        }
        
        for (let file = 0; file <= maxFile; file++) {
            positions.push(file);
        }
        
        // Check if MAX+0.5 is valid on empty rank
        if (isValidInsertPosition(rank, maxFile + 0.5, draggedIcon, icons, layout, isFolder, currentScreen)) {
            positions.push(maxFile + 0.5);
        }
    } else {
        // Add insert-before if file 0 is occupied
        if (rankIcons[0].file === 0 && isValidInsertPosition(rank, -0.5, draggedIcon, icons, layout, isFolder, currentScreen)) {
            positions.push(-0.5);
        }
        
        // Add positions for each icon and between icons
        for (let i = 0; i < rankIcons.length; i++) {
            const file = rankIcons[i].file;
            
            // Add empty slots before this icon
            const prevFile = i > 0 ? rankIcons[i - 1].file : -1;
            for (let emptyFile = prevFile + 1; emptyFile < file; emptyFile++) {
                positions.push(emptyFile);
            }
            
            // Add the icon position itself (if not where we're dragging from)
            positions.push(file);
            
            // Add insert-between if next icon exists
            if (i < rankIcons.length - 1) {
                const nextFile = rankIcons[i + 1].file;
                if (nextFile === file + 1 && isValidInsertPosition(rank, file + 0.5, draggedIcon, icons, layout, isFolder, currentScreen)) {
                    positions.push(file + 0.5);
                }
            }
        }
        
        // Add empty slots after last icon
        const lastFile = rankIcons[rankIcons.length - 1].file;
        for (let emptyFile = lastFile + 1; emptyFile <= maxFile; emptyFile++) {
            positions.push(emptyFile);
        }
        
        // Add insert-after if last file is at maxFile
        if (lastFile === maxFile && isValidInsertPosition(rank, maxFile + 0.5, draggedIcon, icons, layout, isFolder, currentScreen)) {
            positions.push(maxFile + 0.5);
        }
    }
    
    // Right scroll zone: blocked ONLY if we're on the right temporary screen
    if (tempSide !== 'right') {
        positions.push(maxFile + 1);
    }
    
    return positions;
}

// ===== INITIAL CURSOR POSITIONING =====

/**
 * Get initial cursor position when activating keyboard navigation
 */
export function getInitialCursorPosition(direction, icons, layout, isFolder, currentScreen) {
    const minRank = getMinOccupiedRank(icons, layout, isFolder, currentScreen);
    const maxRank = getMaxOccupiedRank(icons, layout, isFolder, currentScreen);
    const minFile = getMinOccupiedFile(icons, layout, isFolder, currentScreen);
    const maxFile = getMaxOccupiedFile(icons, layout, isFolder, currentScreen);
    
    switch (direction) {
        case 'up':
            return { rank: maxRank ?? 0, file: minFile ?? 0 };
        case 'down':
            return { rank: minRank ?? 0, file: minFile ?? 0 };
        case 'left':
            return { rank: minRank ?? 0, file: maxFile ?? 0 };
        case 'right':
            return { rank: minRank ?? 0, file: minFile ?? 0 };
        default:
            return { rank: minRank ?? 0, file: minFile ?? 0 };
    }
}

// ===== PIXEL TO RANK/FILE CONVERSION =====

/**
 * Convert pixel coordinates (clientX, clientY) to rank/file coordinates
 * This enables mouse/touch events to participate in the logical coordinate system
 * 
 * Returns { rank, file } or null if outside the grid
 * Can return fractional files for positions between icons
 * Can return special ranks (-1, -0.5, MAX+1) for modal zones
 */
export function pixelToRankFile(clientX, clientY, layout, isFolder) {
    // Get viewport info
    const viewportEl = isFolder ? 
        document.getElementById('folderContainer') : 
        document.getElementById('appspaceViewport');
    
    if (!viewportEl) return null;
    
    const viewportRect = viewportEl.getBoundingClientRect();
    
    // Convert to viewport-relative coordinates
    const relativeX = clientX - viewportRect.left;
    const relativeY = clientY - viewportRect.top;
    
    // Get dimensions
    const iconSize = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--icon-size')
    ) || 80;
    
    const gapH = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')
    ) || 10;
    
    let gapV;
    if (isFolder) {
        // Folders use a different vertical gap
        gapV = Math.floor(iconSize * 0.15); // Match LAYOUT_CONFIG.folderGapVerticalRatio
    } else {
        gapV = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-vertical')
        ) || 10;
    }
    
    const margin = layout.margin || 0;
    const textHeight = 20;
    
    // Calculate horizontal offset based on centering
    let horizontalOffset = margin;
    if (!isFolder) {
        // Main screen: content is centered within viewport
        const regionWidth = (iconSize * 4) + (gapH * 3);
        const contentWidth = (regionWidth * layout.leaves) + (layout.regionGapH * (layout.leaves - 1)) + (2 * margin);
        horizontalOffset = (viewportRect.width - contentWidth) / 2 + margin;
    }
    
    // Adjust for offsets
    const gridX = relativeX - horizontalOffset;
    const gridY = relativeY - margin;
    
    // Check for modal zones in folder view
    if (isFolder) {
        const maxRank = getMaxRank(layout, isFolder, false);
        const totalFiles = layout.leaves * 4;
        
        // Calculate grid bounds
        const regionWidth = (iconSize * 4) + (gapH * 3);
        const regionHeight = (iconSize + textHeight) * 3 + (gapV * 2);
        const totalWidth = (regionWidth * layout.leaves) + (layout.regionGapH * (layout.leaves - 1));
        
        // Check if above grid (rank -1 or -0.5)
        if (gridY < 0) {
            // Modal overlay above folder
            return { rank: -1, file: 0 };
        }
        
        // Check if below grid (rank MAX+1)
        if (gridY > regionHeight) {
            // Modal overlay below folder
            return { rank: maxRank + 1, file: 0 };
        }
    }
    
    // Calculate which file (column)
    const totalFiles = layout.leaves * 4;
    let file = null;
    let fileGapInfo = null; // Track if we're in a horizontal gap
    let xInGrid = gridX;
    
    // Calculate first file position (always at 0)
    const firstFileX = 0;
    
    // Check for gap before file 0 (file -0.5)
    // This is the gap-width space to the left of file 0's icon
    if (xInGrid >= -gapH && xInGrid < firstFileX) {
        file = -0.5;
    } 
    // Check for left scroll zone (further left than the -0.5 gap)
    else if (xInGrid < -gapH) {
        return { rank: 0, file: -1 };
    } 
    else {
        // Iterate through files to find which one we're in
        for (let f = 0; f < totalFiles; f++) {
            const leafIndex = Math.floor(f / 4);
            const fileInLeaf = f % 4;
            
            // Calculate x position of this file
            const regionWidth = (iconSize * 4) + (gapH * 3);
            const leafOffset = leafIndex * (regionWidth + layout.regionGapH);
            const fileOffset = fileInLeaf * (iconSize + gapH);
            const fileX = leafOffset + fileOffset;
            
            const fileRight = fileX + iconSize;
            
            if (xInGrid >= fileX && xInGrid <= fileRight) {
                // Inside this file
                file = f;
                break;
            } else if (f < totalFiles - 1) {
                // Determine if next file is in same region or next region
                const nextLeafIndex = Math.floor((f + 1) / 4);
                const isRegionBoundary = nextLeafIndex > leafIndex;
                
                // Calculate where the next file starts
                let nextFileX;
                if (isRegionBoundary) {
                    // Next file is in next region
                    // The gap is ONLY regionGapH (not gapH + regionGapH)
                    // because the leafOffset calculation already accounts for spacing
                    nextFileX = fileX + iconSize + layout.regionGapH;
                    console.log('Region boundary detected:', {
                        isFolder,
                        f,
                        fileX,
                        fileRight,
                        nextFileX,
                        iconSize,
                        regionGapH: layout.regionGapH,
                        calculatedGapWidth: nextFileX - fileRight
                    });
                } else {
                    // Next file is in same region - just normal gap
                    nextFileX = fileX + iconSize + gapH;
                }
                
                console.log('Gap check:', {
                    f,
                    isRegionBoundary,
                    xInGrid,
                    fileRight,
                    nextFileX,
                    inGap: xInGrid > fileRight && xInGrid < nextFileX
                });
                
                if (xInGrid > fileRight && xInGrid < nextFileX) {
                    // We're in a gap
                    if (isRegionBoundary) {
                        // In region gap - store info for smart selection
                        fileGapInfo = {
                            leftFile: f,
                            rightFile: f + 1,
                            distToLeft: xInGrid - fileRight,
                            distToRight: nextFileX - xInGrid,
                            isRegionGap: true
                        };
                    } else {
                        // In normal gap between icons - return fractional file
                        file = f + 0.5;
                        break;
                    }
                    break;
                }
            }
        }
    }
    
    // Check for gap after last file (file MAX+0.5) or right scroll zone
    if (file === null && !fileGapInfo && xInGrid > 0) {
        const regionWidth = (iconSize * 4) + (gapH * 3);
        const totalWidth = (regionWidth * layout.leaves) + (layout.regionGapH * (layout.leaves - 1));
        
        // Calculate last file position
        const lastFile = totalFiles - 1;
        const lastLeafIndex = Math.floor(lastFile / 4);
        const lastFileInLeaf = lastFile % 4;
        const lastLeafOffset = lastLeafIndex * (regionWidth + layout.regionGapH);
        const lastFileOffset = lastFileInLeaf * (iconSize + gapH);
        const lastFileX = lastLeafOffset + lastFileOffset;
        const lastFileRight = lastFileX + iconSize;
        
        // Check if in the gap after the last file (before right scroll zone)
        if (xInGrid > lastFileRight && xInGrid <= (lastFileRight + gapH)) {
            file = lastFile + 0.5;
        } else if (xInGrid > totalWidth) {
            // Right scroll zone
            return { rank: 0, file: totalFiles };
        } else {
            // Default to last file if we're past the grid
            file = totalFiles - 1;
        }
    }
    
    if (file === null && !fileGapInfo) return null;
    
    // Calculate which rank (row)
    let rank = null;
    let rankGapInfo = null; // Track if we're in a vertical gap
    let yInGrid = gridY;
    
    if (isFolder) {
        // Folder: single band, 3 ranks
        const rankHeight = iconSize + textHeight;
        for (let r = 0; r < 3; r++) {
            const rankY = r * (rankHeight + gapV);
            const rankBottom = rankY + rankHeight;
            
            if (yInGrid >= rankY && yInGrid <= rankBottom) {
                rank = r;
                break;
            } else if (r < 2) {
                // Check if in gap between this rank and next
                const nextRankY = (r + 1) * (rankHeight + gapV);
                if (yInGrid > rankBottom && yInGrid < nextRankY) {
                    // In vertical gap
                    rankGapInfo = {
                        aboveRank: r,
                        belowRank: r + 1,
                        distToAbove: yInGrid - rankBottom,
                        distToBelow: nextRankY - yInGrid
                    };
                    break;
                }
            }
        }
        
        if (rank === null && !rankGapInfo) {
            // Default to closest rank
            if (yInGrid < 0) rank = 0;
            else rank = 2;
        }
        
        // If we're in a vertical gap but also need a rank for horizontal gap checking,
        // set rank to the nearer of the two adjacent ranks
        if (rank === null && rankGapInfo) {
            rank = rankGapInfo.distToAbove < rankGapInfo.distToBelow ? 
                rankGapInfo.aboveRank : rankGapInfo.belowRank;
        }
    } else {
        // Main screen: multiple bands
        const totalRanks = layout.bands * 3;
        const rankHeight = iconSize + textHeight;
        
        for (let r = 0; r < totalRanks; r++) {
            const bandIndex = Math.floor(r / 3);
            const rankInBand = r % 3;
            
            const bandOffset = bandIndex * ((rankHeight * 3) + (gapV * 2) + layout.regionGapV);
            const rankOffset = rankInBand * (rankHeight + gapV);
            const rankY = bandOffset + rankOffset;
            const rankBottom = rankY + rankHeight;
            
            if (yInGrid >= rankY && yInGrid <= rankBottom) {
                rank = r;
                break;
            } else if (r < totalRanks - 1) {
                // Determine if next rank is in same band or next band
                const nextBandIndex = Math.floor((r + 1) / 3);
                const isBandBoundary = nextBandIndex > bandIndex;
                
                // Calculate where next rank starts
                let nextRankY;
                if (isBandBoundary) {
                    // Next rank is in next band
                    const nextBandOffset = nextBandIndex * ((rankHeight * 3) + (gapV * 2) + layout.regionGapV);
                    nextRankY = nextBandOffset;
                } else {
                    // Next rank is in same band
                    nextRankY = bandOffset + ((rankInBand + 1) * (rankHeight + gapV));
                }
                
                if (yInGrid > rankBottom && yInGrid < nextRankY) {
                    // In vertical gap
                    rankGapInfo = {
                        aboveRank: r,
                        belowRank: r + 1,
                        distToAbove: yInGrid - rankBottom,
                        distToBelow: nextRankY - yInGrid,
                        isBandGap: isBandBoundary
                    };
                    break;
                }
            }
        }
        
        if (rank === null && !rankGapInfo) {
            // Default to closest rank
            const maxRank = (layout.bands * 3) - 1;
            if (yInGrid < 0) rank = 0;
            else rank = maxRank;
        }
        
        // If we're in a vertical gap but also need a rank for horizontal gap checking,
        // set rank to the nearer of the two adjacent ranks
        if (rank === null && rankGapInfo) {
            rank = rankGapInfo.distToAbove < rankGapInfo.distToBelow ? 
                rankGapInfo.aboveRank : rankGapInfo.belowRank;
        }
    }
    
    // Handle gaps intelligently
    // Handle gaps intelligently
    // At this point, normal insert points (within regions) have already been handled
    // We only need to deal with region gaps and vertical gaps
    
    console.log('End of pixelToRankFile:', { rank, file, hasFileGapInfo: !!fileGapInfo, hasRankGapInfo: !!rankGapInfo });
    
    if (fileGapInfo) {
        console.log('Processing fileGapInfo:', { fileGapInfo, rank, isFolder });
        
        // We're in a region gap - check if it should be a valid insert point
        // Use the same approach as drag code - filter window.icons by folder
        const icons = isFolder ? 
            window.icons.filter(icon => icon.kfolder === state.openFolder.id) :
            window.icons.filter(icon => icon.kfolder === 0);
        
        console.log('Icons check:', { isFolder, hasIcons: !!icons, iconsLength: icons?.length });
        
        if (icons && icons.length > 0) {
            if (rankGapInfo) {
                // If also in vertical gap, check both ranks
                const aboveLeftIcon = helpers.getIconAtRankFile(rankGapInfo.aboveRank, fileGapInfo.leftFile, icons, layout, isFolder);
                const aboveRightIcon = helpers.getIconAtRankFile(rankGapInfo.aboveRank, fileGapInfo.rightFile, icons, layout, isFolder);
                const belowLeftIcon = helpers.getIconAtRankFile(rankGapInfo.belowRank, fileGapInfo.leftFile, icons, layout, isFolder);
                const belowRightIcon = helpers.getIconAtRankFile(rankGapInfo.belowRank, fileGapInfo.rightFile, icons, layout, isFolder);
                
                // If above rank has both sides occupied, use that for insert
                if (aboveLeftIcon && aboveRightIcon) {
                    const fractionalFile = fileGapInfo.leftFile + 0.5;
                    console.log('RETURNING FRACTIONAL FILE (above):', { rank: rankGapInfo.aboveRank, file: fractionalFile });
                    return { rank: rankGapInfo.aboveRank, file: fractionalFile };
                }
                // If below rank has both sides occupied, use that for insert
                if (belowLeftIcon && belowRightIcon) {
                    const fractionalFile = fileGapInfo.leftFile + 0.5;
                    console.log('RETURNING FRACTIONAL FILE (below):', { rank: rankGapInfo.belowRank, file: fractionalFile });
                    return { rank: rankGapInfo.belowRank, file: fractionalFile };
                }
            } else {
                // Not in vertical gap, just check the current rank
                const leftIcon = helpers.getIconAtRankFile(rank, fileGapInfo.leftFile, icons, layout, isFolder);
                const rightIcon = helpers.getIconAtRankFile(rank, fileGapInfo.rightFile, icons, layout, isFolder);
                
                console.log('Checking icons at region gap:', {
                    rank,
                    leftFile: fileGapInfo.leftFile,
                    rightFile: fileGapInfo.rightFile,
                    leftIcon: !!leftIcon,
                    rightIcon: !!rightIcon
                });
                
                // If both adjacent files are occupied, this is a valid insert point
                if (leftIcon && rightIcon) {
                    const fractionalFile = fileGapInfo.leftFile + 0.5;
                    console.log('RETURNING FRACTIONAL FILE:', { rank, file: fractionalFile });
                    return { rank, file: fractionalFile };
                } else {
                    console.log('Not both occupied - leftIcon:', leftIcon?.id, 'rightIcon:', rightIcon?.id);
                }
            }
        } else {
            console.log('No icons or empty array');
        }
        
        // Not a valid insert point - use smart slot selection
        console.log('Falling back to smart slot selection');
        const result = selectBestSlotForGap(rank, file, rankGapInfo, fileGapInfo, layout, isFolder);
        console.log('Smart selection result:', result);
        return result;
    }
    
    // Only vertical gap - use smart slot selection
    if (rankGapInfo) {
        return selectBestSlotForGap(rank, file, rankGapInfo, fileGapInfo, layout, isFolder);
    }
    
    return { rank, file };
}

/**
 * Helper function to intelligently select the best icon slot when cursor is in a gap
 * Prefers empty slots, then nearest slot
 */
function selectBestSlotForGap(rank, file, rankGapInfo, fileGapInfo, layout, isFolder) {
    // Safely get icons array - use same approach as drag code
    const icons = isFolder ? 
        window.icons.filter(icon => icon.kfolder === state.openFolder.id) :
        window.icons.filter(icon => icon.kfolder === 0);
    
    // If we can't get icons, just return the nearest slot without checking occupancy
    const canCheckOccupancy = icons && icons.length > 0;
    
    let candidates = [];
    
    // Determine which slots are adjacent to the gap
    if (rankGapInfo && fileGapInfo) {
        // Corner case: in both vertical and horizontal gap
        // Check all four surrounding slots
        candidates = [
            { rank: rankGapInfo.aboveRank, file: fileGapInfo.leftFile, 
              dist: Math.sqrt(Math.pow(rankGapInfo.distToAbove, 2) + Math.pow(fileGapInfo.distToLeft, 2)) },
            { rank: rankGapInfo.aboveRank, file: fileGapInfo.rightFile, 
              dist: Math.sqrt(Math.pow(rankGapInfo.distToAbove, 2) + Math.pow(fileGapInfo.distToRight, 2)) },
            { rank: rankGapInfo.belowRank, file: fileGapInfo.leftFile, 
              dist: Math.sqrt(Math.pow(rankGapInfo.distToBelow, 2) + Math.pow(fileGapInfo.distToLeft, 2)) },
            { rank: rankGapInfo.belowRank, file: fileGapInfo.rightFile, 
              dist: Math.sqrt(Math.pow(rankGapInfo.distToBelow, 2) + Math.pow(fileGapInfo.distToRight, 2)) }
        ];
    } else if (rankGapInfo) {
        // Vertical gap only - check slots above and below
        candidates = [
            { rank: rankGapInfo.aboveRank, file: file, dist: rankGapInfo.distToAbove },
            { rank: rankGapInfo.belowRank, file: file, dist: rankGapInfo.distToBelow }
        ];
    } else if (fileGapInfo) {
        // Horizontal gap only - check slots left and right
        candidates = [
            { rank: rank, file: fileGapInfo.leftFile, dist: fileGapInfo.distToLeft },
            { rank: rank, file: fileGapInfo.rightFile, dist: fileGapInfo.distToRight }
        ];
    } else {
        return { rank, file };
    }
    
    if (!canCheckOccupancy) {
        // Can't check occupancy, just return nearest slot by distance
        const bestSlot = candidates.reduce((nearest, current) => 
            current.dist < nearest.dist ? current : nearest
        );
        return { rank: bestSlot.rank, file: bestSlot.file };
    }
    
    // Check which candidates are empty
    const candidatesWithState = candidates.map(c => {
        const icon = helpers.getIconAtRankFile(c.rank, c.file, icons, layout, isFolder);
        return { ...c, isEmpty: !icon };
    });
    
    // Prefer empty slots
    const emptySlots = candidatesWithState.filter(c => c.isEmpty);
    const occupiedSlots = candidatesWithState.filter(c => !c.isEmpty);
    
    let bestSlot;
    if (emptySlots.length > 0) {
        // Choose nearest empty slot
        bestSlot = emptySlots.reduce((nearest, current) => 
            current.dist < nearest.dist ? current : nearest
        );
    } else if (occupiedSlots.length > 0) {
        // All slots occupied, choose nearest
        bestSlot = occupiedSlots.reduce((nearest, current) => 
            current.dist < nearest.dist ? current : nearest
        );
    } else {
        // Fallback
        bestSlot = candidates[0];
    }
    
    return { rank: bestSlot.rank, file: bestSlot.file };
}
